import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import RestoreIcon from '@mui/icons-material/Restore';
import StorageIcon from '@mui/icons-material/Storage';
import FolderIcon from '@mui/icons-material/Folder';
import LayersIcon from '@mui/icons-material/Layers';
import axiosInstance from '../services/axiosInstance';
import { API_BASE_URL } from '../config';

// Backup-typ
interface Backup {
  _id: string;
  name: string;
  description?: string;
  createdAt: string;
  type: 'full' | 'db-only' | 'files-only' | 'complete-system';
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
  filePath?: string;
  fileSize?: number;
  error?: string;
  metadata?: {
    assetCount?: number;
    categoryCount?: number;
    userCount?: number;
    collectionCount?: number;
    templateCount?: number;
    apiKeyCount?: number;
    fileSize?: number;
    [key: string]: any;
  };
}

// Formatera filstorlek till läsbart format
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Okänd storlek';
  
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;
  
  if (bytes < KB) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / KB).toFixed(2)} KB`;
  if (bytes < GB) return `${(bytes / MB).toFixed(2)} MB`;
  return `${(bytes / GB).toFixed(2)} GB`;
};

// Formatera datum
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const BackupManagement: React.FC = () => {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openNewBackupDialog, setOpenNewBackupDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<Backup | null>(null);
  const [backupName, setBackupName] = useState('');
  const [backupDescription, setBackupDescription] = useState('');
  const [backupType, setBackupType] = useState<'full' | 'db-only' | 'files-only' | 'complete-system'>('full');
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Hämta alla backups
  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/backups');
      setBackups(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching backups:', err);
      if (err.response?.status === 403) {
        setError('Du har inte behörighet att hantera backuper. Kontakta din systemadministratör.');
      } else {
        setError(err.response?.data?.message || 'Kunde inte ladda backuper');
      }
    } finally {
      setLoading(false);
    }
  };

  // Automatisk refresh av backup-status
  useEffect(() => {
    const hasInProgressBackups = backups.some(backup => backup.status === 'in-progress');
    
    if (hasInProgressBackups) {
      const interval = setInterval(() => {
        console.log('🔄 Auto-refreshing backup status...');
        fetchBackups();
      }, 5000); // Refresh var 5:e sekund när det finns pågående backups
      
      return () => clearInterval(interval);
    }
  }, [backups]);

  // Ladda backups när komponenten monteras
  useEffect(() => {
    fetchBackups();
  }, []);

  // Hantera skapande av ny backup
  const handleCreateBackup = async () => {
    if (!backupName.trim()) {
      setSnackbarMessage('Backup-namn är obligatoriskt');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Förhindra dubbel-submission
    if (creatingBackup) {
      console.log('⚠️ Backup creation already in progress, ignoring duplicate request');
      return;
    }

    try {
      setCreatingBackup(true);
      console.log(`🚀 Starting backup creation: ${backupName} (${backupType})`);
      
      const response = await axiosInstance.post(`/api/backups/${backupType}`, {
        name: backupName,
        description: backupDescription.trim() || undefined
      });
      
      console.log('✅ Backup creation request sent successfully');
      setBackups([response.data, ...backups]);
      setSnackbarMessage(`Backup "${backupName}" har påbörjats`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Stäng dialogen och återställ formuläret
      setOpenNewBackupDialog(false);
      setBackupName('');
      setBackupDescription('');
      setBackupType('full');
      
      // Uppdatera listan efter en stund för att se statusuppdateringar
      setTimeout(() => {
        fetchBackups();
      }, 2000);
    } catch (err: any) {
      console.error('Error creating backup:', err);
      setSnackbarMessage(err.response?.data?.message || 'Kunde inte skapa backup');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      // Håll knappen disabled lite längre för att förhindra snabba dubbel-klick
      setTimeout(() => {
        setCreatingBackup(false);
        console.log('🔓 Backup creation button re-enabled');
      }, 3000); // 3 sekunder
    }
  };

  // Hantera återställning från backup
  const handleRestore = async () => {
    if (!selectedBackup) return;
    
    try {
      setRestoring(true);
      const response = await axiosInstance.post(`/api/backups/${selectedBackup._id}/restore`);
      
      if (response.data.success) {
        setSnackbarMessage(`Systemet har återställts från backup "${selectedBackup.name}"`);
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(`Fel vid återställning: ${response.data.message}`);
        setSnackbarSeverity('error');
      }
      
      setSnackbarOpen(true);
      setOpenRestoreDialog(false);
      setSelectedBackup(null);
      
      // Uppdatera listan
      fetchBackups();
    } catch (err: any) {
      console.error('Error restoring backup:', err);
      setSnackbarMessage(err.response?.data?.message || 'Kunde inte återställa från backup');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setRestoring(false);
    }
  };

  // Hantera borttagning av backup
  const handleDelete = async () => {
    if (!selectedBackup) return;
    
    try {
      setDeleting(true);
      const response = await axiosInstance.delete(`/api/backups/${selectedBackup._id}`);
      
      if (response.data.success) {
        setBackups(backups.filter(b => b._id !== selectedBackup._id));
        setSnackbarMessage(`Backup "${selectedBackup.name}" har tagits bort`);
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(`Fel vid borttagning: ${response.data.message}`);
        setSnackbarSeverity('error');
      }
      
      setSnackbarOpen(true);
      setOpenDeleteDialog(false);
      setSelectedBackup(null);
    } catch (err: any) {
      console.error('Error deleting backup:', err);
      setSnackbarMessage(err.response?.data?.message || 'Kunde inte ta bort backup');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  // Hantera nedladdning av backup
  const handleDownload = (backup: Backup) => {
    window.open(`${API_BASE_URL}/api/backups/${backup._id}/download`, '_blank');
  };

  // Visa status-chip med rätt färg
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label="Väntar" color="warning" size="small" />;
      case 'in-progress':
        return <Chip label="Pågår" color="info" size="small" icon={<CircularProgress size={12} />} />;
      case 'completed':
        return <Chip label="Slutförd" color="success" size="small" />;
      case 'failed':
        return <Chip label="Misslyckad" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  // Visa typ-ikon
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'complete-system':
        return <Tooltip title="Komplett systembackup"><LayersIcon color="primary" /></Tooltip>;
      case 'full':
        return <Tooltip title="Komplett appdata"><LayersIcon /></Tooltip>;
      case 'db-only':
        return <Tooltip title="Databasbackup"><StorageIcon /></Tooltip>;
      case 'files-only':
        return <Tooltip title="Filbackup"><FolderIcon /></Tooltip>;
      default:
        return null;
    }
  };

  // Visa typ-text
  const getTypeText = (type: string) => {
    switch (type) {
      case 'complete-system':
        return 'Komplett system';
      case 'full':
        return 'Komplett appdata';
      case 'db-only':
        return 'Databas';
      case 'files-only':
        return 'Filer';
      default:
        return type;
    }
  };

  const getTypeDescription = (type: string) => {
    switch (type) {
      case 'complete-system':
        return 'ALLT: Databas + Filer + Källkod + Konfiguration + Dependencies (Verklig full backup!)';
      case 'full':
        return 'Databas + Uppladdade filer (INGEN systemkonfiguration eller kod)';
      case 'db-only':
        return 'Endast databasdata (kategorier, användare, assets-metadata)';
      case 'files-only':
        return 'Endast uppladdade filer (inga databasdata)';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Systembackuper</Typography>
        <Button 
          variant="contained" 
          onClick={() => setOpenNewBackupDialog(true)}
          color="primary"
        >
          Skapa ny backup
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : backups.length === 0 ? (
        <Alert severity="info">
          Inga backuper har skapats än. Skapa din första backup nu.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Namn</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Skapad</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Storlek</TableCell>
                <TableCell>Åtgärder</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.map((backup) => (
                <TableRow key={backup._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1">{backup.name}</Typography>
                      {backup.description && (
                        <Typography variant="caption" color="textSecondary">
                          {backup.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTypeIcon(backup.type)}
                      <Typography variant="body2">{getTypeText(backup.type)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(backup.createdAt)}</TableCell>
                  <TableCell>
                    {getStatusChip(backup.status)}
                    {backup.error && (
                      <Tooltip title={backup.error}>
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          Fel: {backup.error.substring(0, 30)}...
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{formatFileSize(backup.fileSize)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {backup.status === 'completed' && (
                        <>
                          <Tooltip title="Ladda ned">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDownload(backup)}
                              color="primary"
                            >
                              <CloudDownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Återställ">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setSelectedBackup(backup);
                                setOpenRestoreDialog(true);
                              }}
                              color="warning"
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Ta bort">
                        <IconButton 
                          size="small"
                          onClick={() => {
                            setSelectedBackup(backup);
                            setOpenDeleteDialog(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog för att skapa ny backup */}
      <Dialog open={openNewBackupDialog} onClose={() => setOpenNewBackupDialog(false)}>
        <DialogTitle>Skapa ny backup</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Fyll i information för att skapa en ny systembackup.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Backup-namn"
            fullWidth
            variant="outlined"
            value={backupName}
            onChange={(e) => setBackupName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Beskrivning (valfritt)"
            fullWidth
            variant="outlined"
            value={backupDescription}
            onChange={(e) => setBackupDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2">Backup-typ:</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Viktigt:</strong> "Komplett appdata" och lägre inkluderar INTE systemkonfiguration. 
                Endast "Komplett system" backar upp allt inklusive kod och konfiguration.
              </Typography>
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Button 
                  variant={backupType === 'complete-system' ? 'contained' : 'outlined'}
                  onClick={() => setBackupType('complete-system')}
                  startIcon={<LayersIcon />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start', mb: 0.5 }}
                  color="primary"
                >
                  Komplett system ⭐ REKOMMENDERAT
                </Button>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 1 }}>
                  {getTypeDescription('complete-system')}
                </Typography>
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', ml: 1, mt: 0.5 }}>
                  ⚠️ Större filstorlek (inkluderar källkod) men verklig återställning möjlig
                </Typography>
              </Box>
              
              <Box>
                <Button 
                  variant={backupType === 'full' ? 'contained' : 'outlined'}
                  onClick={() => setBackupType('full')}
                  startIcon={<LayersIcon />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start', mb: 0.5 }}
                >
                  Komplett appdata
                </Button>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 1 }}>
                  {getTypeDescription('full')}
                </Typography>
              </Box>
              
              <Box>
                <Button 
                  variant={backupType === 'db-only' ? 'contained' : 'outlined'}
                  onClick={() => setBackupType('db-only')}
                  startIcon={<StorageIcon />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start', mb: 0.5 }}
                >
                  Databas
                </Button>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 1 }}>
                  {getTypeDescription('db-only')}
                </Typography>
              </Box>
              
              <Box>
                <Button 
                  variant={backupType === 'files-only' ? 'contained' : 'outlined'}
                  onClick={() => setBackupType('files-only')}
                  startIcon={<FolderIcon />}
                  fullWidth
                  sx={{ justifyContent: 'flex-start', mb: 0.5 }}
                >
                  Filer
                </Button>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 1 }}>
                  {getTypeDescription('files-only')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewBackupDialog(false)} disabled={creatingBackup}>
            Avbryt
          </Button>
          <Button 
            onClick={handleCreateBackup} 
            variant="contained" 
            color="primary"
            disabled={creatingBackup || !backupName.trim()}
            startIcon={creatingBackup ? <CircularProgress size={16} /> : null}
          >
            {creatingBackup ? 'Skapar backup...' : 'Skapa backup'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog för att återställa från backup */}
      <Dialog open={openRestoreDialog} onClose={() => setOpenRestoreDialog(false)}>
        <DialogTitle>Återställ från backup</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Är du säker på att du vill återställa systemet från denna backup? Detta kommer att ersätta all nuvarande data.
          </DialogContentText>
          {selectedBackup && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Backup-information:</Typography>
              <Typography variant="body2">Namn: {selectedBackup.name}</Typography>
              <Typography variant="body2">Typ: {getTypeText(selectedBackup.type)}</Typography>
              <Typography variant="body2">Skapad: {formatDate(selectedBackup.createdAt)}</Typography>
              {selectedBackup.metadata?.assetCount && (
                <Typography variant="body2">Antal assets: {selectedBackup.metadata.assetCount}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRestoreDialog(false)}>Avbryt</Button>
          <Button 
            onClick={handleRestore} 
            variant="contained" 
            color="warning"
            disabled={restoring}
          >
            {restoring ? <CircularProgress size={24} /> : 'Återställ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog för att ta bort backup */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Ta bort backup</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Är du säker på att du vill ta bort denna backup? Detta kan inte ångras.
          </DialogContentText>
          {selectedBackup && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Backup-information:</Typography>
              <Typography variant="body2">Namn: {selectedBackup.name}</Typography>
              <Typography variant="body2">Typ: {getTypeText(selectedBackup.type)}</Typography>
              <Typography variant="body2">Skapad: {formatDate(selectedBackup.createdAt)}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Avbryt</Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Ta bort'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar för meddelanden */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BackupManagement; 